import { format } from 'date-fns';
import { Loader2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  fetchAnalysisById,
  fetchConversationDynamics,
  fetchSkillsAssessment,
  fetchTranscriptAnalysis,
} from '../../services/meetingAnalysis';
import { SkillAssessment } from '../../types/meetinganalysis';
import { CoachingOverviewTab } from './tabs/CoachingOverviewTab';
import { ConversationDynamicsTab } from './tabs/ConversationDynamicsTab';
import { SkillAnalysisTab } from './tabs/SkillAnalysisTab';
import { TranscriptBreakdownTab } from './tabs/TranscriptBreakdownTab';

export default function MeetingCallResults() {
  const { analysisId } = useParams<{ analysisId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [skillsAssessment, setSkillsAssessment] = useState<SkillAssessment[]>(
    [],
  );
  const [transcriptAnalysis, setTranscriptAnalysis] = useState<any[]>([]);
  const [conversationDynamics, setConversationDynamics] = useState<any>(null);
  const [loadingSkills, setLoadingSkills] = useState(false);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [loadingDynamics, setLoadingDynamics] = useState(false);

  // Load analysis data when component mounts
  useEffect(() => {
    const loadAnalysis = async () => {
      if (!analysisId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Fetch main analysis data
        const result = await fetchAnalysisById(analysisId);
        if (result) {
          setAnalysis(result);
        } else {
          toast.error('Analysis not found. Please try again.');
          navigate('/meetingcall');
        }
      } catch (error) {
        console.error('Error loading analysis:', error);
        toast.error('Failed to load analysis data');
        navigate('/meetingcall');
      } finally {
        setLoading(false);
      }
    };

    loadAnalysis();
  }, [analysisId, navigate]);

  // Load skills assessment in a separate effect
  useEffect(() => {
    const loadSkillsAssessment = async () => {
      // Only load skills if we have an analysisId and aren't already loading
      if (!analysisId || loadingSkills) return;

      // Only fetch if we don't already have the data
      if (skillsAssessment.length > 0) return;

      setLoadingSkills(true);
      try {
        const skills = await fetchSkillsAssessment(analysisId);
        setSkillsAssessment(skills);
      } catch (error) {
        console.error('Error loading skills assessment:', error);
        // Don't show a toast here to avoid disrupting the UI
      } finally {
        setLoadingSkills(false);
      }
    };

    // Start loading skills once analysis data is available
    if (analysis) {
      loadSkillsAssessment();
    }
  }, [analysis, analysisId, skillsAssessment.length, loadingSkills]);

  // Load transcript analysis in a separate effect
  useEffect(() => {
    const loadTranscriptAnalysis = async () => {
      // Only load transcript if we have an analysisId, are on the transcript tab, and aren't already loading
      if (!analysisId || loadingTranscript) return;

      // Only fetch if we don't already have transcript data and it's not an example
      if (transcriptAnalysis.length > 0 || analysisId.startsWith('example-'))
        return;

      setLoadingTranscript(true);
      try {
        const transcript = await fetchTranscriptAnalysis(analysisId);
        setTranscriptAnalysis(transcript);
      } catch (error) {
        console.error('Error loading transcript analysis:', error);
        // Don't show a toast here to avoid disrupting the UI
      } finally {
        setLoadingTranscript(false);
      }
    };

    loadTranscriptAnalysis();
  }, [analysis, analysisId, transcriptAnalysis.length, loadingTranscript]);

  // Load conversation dynamics in a separate effect
  useEffect(() => {
    const loadConversationDynamics = async () => {
      // Only load dynamics if we have an analysisId, are on the dynamics tab, and aren't already loading
      if (!analysisId || loadingDynamics) return;

      // Only fetch if we don't already have dynamics data
      if (conversationDynamics !== null) return;

      setLoadingDynamics(true);
      try {
        const dynamics = await fetchConversationDynamics(analysisId);
        setConversationDynamics(dynamics);
      } catch (error) {
        console.error('Error loading conversation dynamics:', error);
        // Don't show a toast here to avoid disrupting the UI
      } finally {
        setLoadingDynamics(false);
      }
    };

    loadConversationDynamics();
  }, [analysis, analysisId, conversationDynamics, loadingDynamics]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f6f6f6]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-gray-600">Loading analysis results...</p>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f6f6f6]">
        <div className="text-center">
          <p className="text-red-600 mb-4">Analysis not found</p>
          <button
            onClick={() => navigate('/meetingcall')}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
          >
            Return to Upload
          </button>
        </div>
      </div>
    );
  }

  // Get participant names
  const selectedParticipant = analysis.selectedParticipant;
  const otherParticipants = analysis.participants?.filter(
    (p: string) => p !== selectedParticipant,
  );

  // Render different tab content based on the active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'skills':
        return (
          <SkillAnalysisTab
            skillsAssessment={skillsAssessment}
            isLoading={loadingSkills}
          />
        );
      case 'transcript':
        return (
          <TranscriptBreakdownTab
            transcriptAnalysis={transcriptAnalysis}
            isLoading={loadingTranscript}
            selectedParticipant={selectedParticipant}
            otherParticipants={otherParticipants}
          />
        );
      case 'dynamics':
        return (
          <ConversationDynamicsTab
            isLoading={loadingDynamics}
            conversationDynamics={conversationDynamics}
          />
        );
      case 'overview':
      default:
        return (
          <CoachingOverviewTab coachingOverview={analysis.coachingOverview} />
        );
    }
  };

  return (
    <div className="min-h-screen bg-[#f6f6f6] pb-3">
      {/* Header */}
      <div className="bg-white px-6 py-4 border-b border-gray-200">
        <div className="max-w-5xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img
              src="https://coach.hupo.co/logos/icon/orange/icon.svg"
              alt="Hupo AI"
              className="h-6 w-6"
            />

            <span className="text-gray-900 font-medium">Meeting analysis</span>
          </div>

          <button
            className="p-1 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100"
            onClick={() => navigate('/meetingcall')}
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Meeting Summary */}
      <div className="max-w-5xl mx-auto px-6 my-6">
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <div className="flex justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {analysis.participants && analysis.participants.length === 2
                  ? `1-1 with ${
                      analysis.participants.find(
                        (p) => p !== analysis.selectedParticipant,
                      ) || 'Team Member'
                    }`
                  : 'Team meeting'}
              </h1>
              <p className="text-gray-500 text-sm mt-1">
                {' '}
                {format(new Date(analysis.meetingDate), 'd MMM yyyy, HH:mm')}
              </p>
            </div>
            <div className="flex -space-x-2">
              <img
                src="https://randomuser.me/api/portraits/men/1.jpg"
                alt="User"
                className="w-10 h-10 rounded-full border-2 border-white"
              />
              <img
                src="https://randomuser.me/api/portraits/men/2.jpg"
                alt="Team Member"
                className="w-10 h-10 rounded-full border-2 border-white"
              />
            </div>
          </div>

          <div>
            <h2 className="text-m font-medium mb-2">Meeting summary</h2>
            <p className="text-gray-700 text-sm leading-relaxed">
              {analysis.summary}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs and Content */}
      <div className="max-w-5xl mx-auto px-6 mb-6">
        <div className="bg-white rounded-lg shadow-sm">
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <div className="flex">
              <button
                className={`py-4 px-6 text-sm font-medium ${
                  activeTab === 'overview'
                    ? 'border-b-2 border-primary text-primary'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('overview')}
              >
                Coaching overview
              </button>
              <button
                className={`py-4 px-6 text-sm font-medium ${
                  activeTab === 'skills'
                    ? 'border-b-2 border-primary text-primary'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('skills')}
              >
                Skill analysis
              </button>
              <button
                className={`py-4 px-6 text-sm font-medium ${
                  activeTab === 'transcript'
                    ? 'border-b-2 border-primary text-primary'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('transcript')}
              >
                Transcript breakdown
              </button>
              <button
                className={`py-4 px-6 text-sm font-medium ${
                  activeTab === 'dynamics'
                    ? 'border-b-2 border-primary text-primary'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('dynamics')}
              >
                Conversation dynamics
              </button>
            </div>
          </div>

          {/* Tab Content */}
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
}
